// Minimal React bindings over @wagmi/core (see NOTES_FOR_LEAD.md for why not the `wagmi` package).
import { useSyncExternalStore } from 'react';
import { connect, disconnect, getAccount, getConnectors, switchChain, watchAccount, watchConnectors } from '@wagmi/core';
import type { Connector, GetAccountReturnType } from '@wagmi/core';
import { chain } from '../config/chain';
import { debugSet } from '../lib/debug';
import { wagmiConfig } from './config';
import { DEV_CONNECTOR_TYPE } from './devAccounts';

function store<T>(read: () => T, watch: (onChange: () => void) => () => void) {
  let value = read();
  const listeners = new Set<() => void>();
  watch(() => {
    value = read();
    for (const l of listeners) l();
  });
  return {
    get: () => value,
    subscribe(l: () => void) {
      listeners.add(l);
      return () => listeners.delete(l);
    },
  };
}

const accountStore = store<GetAccountReturnType>(
  () => getAccount(wagmiConfig),
  (cb) => watchAccount(wagmiConfig, { onChange: cb }),
);
const connectorStore = store<readonly Connector[]>(
  () => getConnectors(wagmiConfig),
  (cb) => watchConnectors(wagmiConfig, { onChange: cb }),
);

export interface WalletState {
  address: `0x${string}` | undefined;
  status: GetAccountReturnType['status'];
  connector: Connector | undefined;
  /** connected, but to a chain other than the app's */
  wrongChain: boolean;
  /** connected with an in-page anvil dev key (signs without a wallet prompt) */
  isLocalTestAccount: boolean;
}

function derive(a: GetAccountReturnType): WalletState {
  return {
    address: a.address,
    status: a.status,
    connector: a.connector,
    wrongChain: a.status === 'connected' && a.chainId !== chain.id,
    isLocalTestAccount: a.connector?.type === DEV_CONNECTOR_TYPE,
  };
}

let derived = derive(accountStore.get());
accountStore.subscribe(() => {
  derived = derive(accountStore.get());
  debugSet('wallet', { address: derived.address, status: derived.status, connector: derived.connector?.name, wrongChain: derived.wrongChain });
});
debugSet('wallet', { address: derived.address, status: derived.status });

export function useWallet(): WalletState {
  return useSyncExternalStore(accountStore.subscribe, () => derived);
}

export function useConnectors(): readonly Connector[] {
  return useSyncExternalStore(connectorStore.subscribe, connectorStore.get);
}

export const connectWallet = (connector: Connector) => connect(wagmiConfig, { connector, chainId: chain.id });
export const disconnectWallet = () => disconnect(wagmiConfig);
export const switchToAppChain = () => switchChain(wagmiConfig, { chainId: chain.id });
